import { useState } from "react";
import { ArrowUpRight, CircleHelp } from "lucide-react";
import { MerrySegmentedSwitch } from "@/shared/ui/MerrySegmentedSwitch";
import summaryNotificationIcon from "@/assets/figma/store-overview/summary-notification.svg";
import type { OverviewPanelData } from "./overview-panel-data";
import { KpiSection } from "./kpi-section";
import { ContributionRanking } from "./contribution-ranking";
import { DragDownList } from "./drag-down-list";
import { TrendChart } from "./trend-chart";
import { PlatformStructure } from "./platform-structure";

export function OverviewPanel({ data }: { data: OverviewPanelData }) {
	const [rankingMetric, setRankingMetric] = useState<
		"gmv" | "profit" | "growth" | "efficiency"
	>("gmv");
	return (
		<div className="overview-panel-content">
			<div className="grid grid-cols-1 overflow-hidden rounded-2xl bg-slate-900/[0.035] md:grid-cols-[1.6fr_1fr_1fr_1fr]">
				<div className="flex flex-col gap-1.5 p-3">
					<p className="flex items-center gap-1.5 text-xs font-semibold text-slate-900">
						<img alt="" aria-hidden="true" src={summaryNotificationIcon} />
						今日大盘 GMV {data.headline.gmv}{data.headline.gmvDelta ? `，${data.headline.gmvDelta}` : ""}
					</p>
					<p className="text-[11px] leading-relaxed text-slate-500">
						{data.headline.summary}
					</p>
				</div>
				{data.alertCounts.map((alert) => (
					<div key={alert.label} className="flex flex-col gap-1.5 p-3">
						<span className="text-[10px] text-slate-400">{alert.label}</span>
						<span className="text-lg font-semibold tabular-nums text-slate-900">
							{alert.value ?? "—"}
						</span>
					</div>
				))}
			</div>

			<KpiSection kpis={data.kpis} />

			<div className="overview-panel-grid">
				<section className="overview-panel-card overview-panel-card--ranking">
					<header className="overview-panel-card__header">
						<div>
							<h2>贡献排名</h2>
							<p>按全店销售额贡献排序</p>
						</div>
						<MerrySegmentedSwitch
              aria-label="排名指标"
              size={34}
              value={rankingMetric}
              onChange={setRankingMetric}
              options={[
                { value: "gmv", label: "GMV" },
                { value: "profit", label: "毛利" },
                { value: "growth", label: "环比增长" },
                { value: "efficiency", label: "广告效率" },
              ]}
            />
					</header>
					<ContributionRanking rows={data.stores} metric={rankingMetric} />
				</section>

				<section className="overview-panel-card overview-panel-card--attention">
					<header className="overview-panel-card__header">
						<div>
							<h2>
								<ArrowUpRight size={14} aria-hidden="true" />
								拖后腿榜 Top 3
							</h2>
							<p>按增速或 ROAS 倒序</p>
						</div>
						<span>Top 3</span>
					</header>
					<DragDownList items={data.attention.slice(0, 3)} />
					{data.attention.length === 0 && (
						<p className="overview-panel-empty">
							<CircleHelp size={14} aria-hidden="true" />
							当前没有需要优先处理的事项
						</p>
					)}
				</section>

				<section className="overview-panel-card overview-panel-card--trend">
					<header className="overview-panel-card__header">
						<div>
							<h2>经营趋势</h2>
							<p>销售额与广告消耗</p>
						</div>
					</header>
					<TrendChart series={data.series} />
				</section>

				<section className="overview-panel-card overview-panel-card--structure">
					<header className="overview-panel-card__header">
						<div>
							<h2>平台结构</h2>
							<p>按平台或市场查看销售分布</p>
						</div>
					</header>
					<PlatformStructure platform={data.platform} market={data.market} />
				</section>
			</div>
		</div>
	);
}
